import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, ShoppingCart, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "sonner";

// Modal voor admin om formules + opties toe te voegen of weg te halen op een lopend project.
// Recalcule prijs gebeurt server-side; preview tonen we live.
export default function ItemsEditorModal({ open, onClose, request, products, onSaved }) {
    // local working copy of items
    const [items, setItems] = useState([]);
    const [customItems, setCustomItems] = useState([]);
    const [newLabel, setNewLabel] = useState("");
    const [newPrice, setNewPrice] = useState("");
    const [saving, setSaving] = useState(false);
    const [manualPrice, setManualPrice] = useState("");
    const [manualDirty, setManualDirty] = useState(false);

    useEffect(() => {
        if (open) {
            setItems((request?.items || []).map((it) => ({
                product_id: it.product_id,
                option_ids: [...(it.option_ids || [])],
                tier_id: it.tier_id || null,
            })));
            setCustomItems((request?.custom_items || []).map((c) => ({ ...c })));
            setNewLabel("");
            setNewPrice("");
            if (request?.manual_total !== null && request?.manual_total !== undefined) {
                setManualPrice(String(request.manual_total));
                setManualDirty(true);
            } else {
                setManualPrice("");
                setManualDirty(false);
            }
        }
    }, [open, request]);

    const addedProductIds = items.map((i) => i.product_id);
    const photoCount = request?.photo_urls?.length || 0;

    const computePrice = useMemo(() => {
        let total = 0;
        for (const it of items) {
            const p = products.find((x) => x.id === it.product_id);
            if (!p) continue;
            let base = p.price;
            if (p.photo_tiers && p.photo_tiers.length) {
                const picked = it.tier_id
                    ? p.photo_tiers.find((t) => t.id === it.tier_id)
                    : p.photo_tiers.find((t) => photoCount >= t.min_photos && photoCount <= t.max_photos);
                if (picked) base = picked.price;
            }
            total += base;
            for (const oid of it.option_ids || []) {
                const o = (p.options || []).find((o) => o.id === oid);
                if (o) total += Number(o.price);
            }
        }
        for (const c of customItems) total += Number(c.price) || 0;
        return total + (request?.travel_cost || 0);
    }, [items, customItems, products, photoCount, request?.travel_cost]);

    const oldTotal = Number(request?.total_price || 0);
    const parsedManual = parseFloat(String(manualPrice).replace(",", "."));
    const manualActive = manualDirty && !isNaN(parsedManual) && parsedManual >= 0 && +parsedManual.toFixed(2) !== +computePrice.toFixed(2);
    const finalTotal = manualActive ? +parsedManual.toFixed(2) : computePrice;
    const diff = +(finalTotal - oldTotal).toFixed(2);

    const addProduct = (productId) => {
        const p = products.find((x) => x.id === productId);
        if (!p) return;
        const newItem = { product_id: productId, option_ids: [], tier_id: null };
        if (p.photo_tiers && p.photo_tiers.length) {
            const picked = p.photo_tiers.find((t) => photoCount >= t.min_photos && photoCount <= t.max_photos);
            if (picked) newItem.tier_id = picked.id;
        }
        setItems((prev) => [...prev, newItem]);
    };

    const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

    const addCustomItem = () => {
        const label = newLabel.trim();
        const price = parseFloat(String(newPrice).replace(",", "."));
        if (!label) return toast.error("Geef een omschrijving op");
        if (isNaN(price) || price < 0) return toast.error("Geef een geldige prijs op");
        setCustomItems((prev) => [...prev, { label, price: +price.toFixed(2) }]);
        setNewLabel("");
        setNewPrice("");
    };

    const removeCustomItem = (idx) => setCustomItems((prev) => prev.filter((_, i) => i !== idx));

    const toggleOption = (idx, optionId) => {
        setItems((prev) => prev.map((it, i) => {
            if (i !== idx) return it;
            const has = it.option_ids.includes(optionId);
            return { ...it, option_ids: has ? it.option_ids.filter((o) => o !== optionId) : [...it.option_ids, optionId] };
        }));
    };

    const setTier = (idx, tierId) => {
        setItems((prev) => prev.map((it, i) => i === idx ? { ...it, tier_id: tierId } : it));
    };

    const save = async () => {
        if (!items.length) return toast.error("Een project moet minstens één formule bevatten");
        setSaving(true);
        try {
            const payload = { items, custom_items: customItems, manual_total: manualActive ? +parsedManual.toFixed(2) : null };
            const { data } = await api.patch(`/requests/${request.id}/items`, payload);
            toast.success(`Project bijgewerkt — nieuw totaal €${data.total_price.toFixed(2)}`);
            onSaved?.(data);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        }
        setSaving(false);
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[160] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => !saving && onClose()} data-testid="items-editor-modal">
            <div className="bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-md max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <ShoppingCart className="h-5 w-5 text-[#E5A93D]" />
                        <h3 className="font-display font-bold text-xl">Formules & opties bewerken</h3>
                    </div>
                    <p className="text-xs text-zinc-500 mb-5">
                        Voeg achteraf nog een product of optie toe (of haal er één weg). De makelaar krijgt automatisch een melding van het nieuwe totaal.
                    </p>

                    {/* Already added items */}
                    <div className="space-y-3 mb-5">
                        {items.length === 0 && <div className="text-sm text-zinc-500 italic py-3">Nog geen formules — voeg er eentje toe hieronder.</div>}
                        {items.map((it, idx) => {
                            const p = products.find((x) => x.id === it.product_id);
                            if (!p) return null;
                            return (
                                <div key={idx} className="bg-[#F4F4F5] border border-zinc-200 rounded-md p-4" data-testid={`edit-item-${idx}`}>
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.name}</div>
                                            <div className="font-medium text-zinc-950">{p.tagline || p.name}</div>
                                        </div>
                                        <button onClick={() => removeItem(idx)} className="text-red-400/70 hover:text-red-600 p-1.5" title="Verwijder formule" data-testid={`remove-item-${idx}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                    {p.photo_tiers && p.photo_tiers.length > 0 && (
                                        <div className="mb-3">
                                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Foto-tier</div>
                                            <div className="flex flex-wrap gap-2">
                                                {p.photo_tiers.map((t) => (
                                                    <button
                                                        key={t.id}
                                                        type="button"
                                                        onClick={() => setTier(idx, t.id)}
                                                        className={`px-3 py-1.5 text-xs rounded-sm border transition ${it.tier_id === t.id ? "bg-[#E5A93D]/15 border-[#E5A93D] text-zinc-950" : "bg-[#FFFFFF] border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}
                                                        data-testid={`tier-${idx}-${t.id}`}
                                                    >
                                                        {t.label || `${t.min_photos}-${t.max_photos} foto's`} — €{Number(t.price).toFixed(2)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {p.options && p.options.length > 0 && (
                                        <div>
                                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Opties</div>
                                            <div className="space-y-1">
                                                {p.options.map((o) => {
                                                    const checked = it.option_ids.includes(o.id);
                                                    return (
                                                        <label key={o.id} className={`flex items-center gap-3 p-2 rounded-sm cursor-pointer transition ${checked ? "bg-[#E5A93D]/10 border border-[#E5A93D]/30" : "bg-[#FFFFFF] border border-zinc-100 hover:border-zinc-300"}`} data-testid={`option-${idx}-${o.id}`}>
                                                            <Checkbox
                                                                checked={checked}
                                                                onCheckedChange={() => toggleOption(idx, o.id)}
                                                                className="border-zinc-300 data-[state=checked]:bg-[#E5A93D] data-[state=checked]:border-[#E5A93D]"
                                                            />
                                                            <div className="flex-1 text-sm text-zinc-800">{o.label}</div>
                                                            <div className="text-sm text-[#E5A93D] font-medium">€{Number(o.price).toFixed(2)}</div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {(!p.options || p.options.length === 0) && (!p.photo_tiers || p.photo_tiers.length === 0) && (
                                        <div className="text-xs text-zinc-500 italic">Geen opties beschikbaar voor deze formule.</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Add a new product */}
                    <div className="border-t border-zinc-100 pt-4 mb-5">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Voeg een formule toe</div>
                        <div className="flex flex-wrap gap-2">
                            {products.filter((p) => !addedProductIds.includes(p.id)).map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => addProduct(p.id)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-200 text-zinc-600 hover:border-[#E5A93D]/40 hover:bg-[#E5A93D]/5 hover:text-zinc-950 rounded-sm transition"
                                    data-testid={`add-product-${p.id}`}
                                >
                                    <Plus className="h-3 w-3" /> {p.name}
                                </button>
                            ))}
                            {products.filter((p) => !addedProductIds.includes(p.id)).length === 0 && (
                                <div className="text-xs text-zinc-500 italic">Alle beschikbare formules zijn reeds toegevoegd.</div>
                            )}
                        </div>
                    </div>

                    {/* Custom items (vrij te typen extra's) */}
                    <div className="border-t border-zinc-100 pt-4 mb-5">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Eigen product / dienst</div>
                        {customItems.length > 0 && (
                            <div className="space-y-1.5 mb-3">
                                {customItems.map((c, idx) => (
                                    <div key={idx} className="flex items-center gap-3 bg-[#F4F4F5] border border-zinc-200 rounded-sm px-3 py-2" data-testid={`custom-item-row-${idx}`}>
                                        <div className="flex-1 text-sm text-zinc-800">{c.label}</div>
                                        <div className="text-sm text-[#E5A93D] font-medium">€{Number(c.price).toFixed(2)}</div>
                                        <button onClick={() => removeCustomItem(idx)} className="text-red-400/70 hover:text-red-600 p-1" title="Verwijder" data-testid={`remove-custom-item-${idx}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && addCustomItem()}
                                placeholder="bv. Aanpassing video"
                                className="flex-1 h-10 px-3 text-sm bg-[#FFFFFF] border border-zinc-200 rounded-sm focus:outline-none focus:border-[#E5A93D]"
                                data-testid="custom-item-label-input"
                            />
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={newPrice}
                                onChange={(e) => setNewPrice(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && addCustomItem()}
                                placeholder="Prijs (excl. BTW)"
                                className="w-40 h-10 px-3 text-sm bg-[#FFFFFF] border border-zinc-200 rounded-sm focus:outline-none focus:border-[#E5A93D]"
                                data-testid="custom-item-price-input"
                            />
                            <button
                                type="button"
                                onClick={addCustomItem}
                                className="inline-flex items-center gap-1.5 px-3 h-10 text-xs border border-[#E5A93D]/40 text-[#E5A93D] hover:bg-[#E5A93D]/10 rounded-sm transition active:scale-95"
                                data-testid="add-custom-item-btn"
                            >
                                <Plus className="h-3.5 w-3.5" /> Toevoegen
                            </button>
                        </div>
                        <p className="text-[11px] text-zinc-400 mt-1.5">Prijs excl. BTW — wordt meegeteld in het totaal en verschijnt op de factuur.</p>
                    </div>

                    {/* Total preview + manuele prijs */}
                    <div className="bg-[#FFFFFF] border border-zinc-100 rounded-sm p-4 mb-5" data-testid="items-edit-summary">
                        <div className="flex justify-between text-sm mb-1">
                            <span className="text-zinc-500">Oud totaal</span>
                            <span className="text-zinc-600">€{oldTotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-zinc-500">Berekend totaal</span>
                            <span className={manualActive ? "text-zinc-400 line-through" : "text-zinc-600"}>€{computePrice.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center gap-3 text-base font-medium mb-1">
                            <span className="text-zinc-950">Nieuw totaal</span>
                            <div className="flex items-center gap-2">
                                {manualActive && (
                                    <button
                                        type="button"
                                        onClick={() => { setManualPrice(""); setManualDirty(false); }}
                                        className="text-[11px] text-zinc-400 hover:text-zinc-950 underline underline-offset-2"
                                        data-testid="manual-price-reset"
                                        title="Terug naar het berekende totaal"
                                    >
                                        herstel
                                    </button>
                                )}
                                <div className="flex items-center gap-1">
                                    <span className="text-[#E5A93D] font-display font-black text-lg">€</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={manualDirty ? manualPrice : computePrice.toFixed(2)}
                                        onChange={(e) => { setManualPrice(e.target.value); setManualDirty(true); }}
                                        className={`w-28 h-9 px-2 text-right font-display font-black text-lg rounded-sm border focus:outline-none focus:border-[#E5A93D] ${manualActive ? "text-[#E5A93D] border-[#E5A93D]/50 bg-[#E5A93D]/5" : "text-[#E5A93D] border-zinc-200 bg-white"}`}
                                        data-testid="manual-total-input"
                                        title="Pas het totaal manueel aan — bv. voor een korting"
                                    />
                                </div>
                            </div>
                        </div>
                        {manualActive && (
                            <div className="text-[11px] text-[#C8922C] mt-1" data-testid="manual-price-note">
                                Prijs manueel vastgezet — wijzigt niet mee met formules tot je op "herstel" klikt.
                            </div>
                        )}
                        {diff !== 0 && (
                            <div className={`text-xs mt-1 ${diff > 0 ? "text-emerald-600" : "text-red-600"}`}>
                                {diff > 0 ? "+" : "−"}€{Math.abs(diff).toFixed(2)} verschil
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2">
                        <Button onClick={onClose} disabled={saving} variant="ghost" className="flex-1 h-11 border border-zinc-200 text-zinc-600 rounded-sm">
                            Annuleren
                        </Button>
                        <Button
                            onClick={save}
                            disabled={saving || items.length === 0}
                            className="flex-1 h-11 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                            data-testid="save-items-btn"
                        >
                            {saving ? "Bezig..." : "Wijzigingen opslaan"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
